import { useEffect, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { Box, alpha, keyframes, styled } from '@mui/material';

const NAVIGATION_FEEDBACK_DELAY = 150
const LINK_NAVIGATION_PENDING_TIMEOUT = 10000

const navigationLineAnimation = keyframes({
    '0%': {
        transform: 'translateX(-100%)',
    },
    '100%': {
        transform: 'translateX(100%)',
    },
})

export const NavigationProgressLine = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: '52px',
    left: 0,
    right: 0,
    height: '5px',
    overflow: 'hidden',
    opacity: 0,
    pointerEvents: 'none',
    transform: 'translateY(-2px)',
    transition: theme.transitions.create(['opacity', 'transform'], {
        duration: theme.transitions.duration.shortest,
    }),
    zIndex: 1301,

    '&::before': {
        animation: `${navigationLineAnimation} 1000ms ease-in-out infinite`,
        background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.accent.main, 0.18)} 18%, ${theme.palette.accent.main} 48%, ${alpha(theme.palette.accent.main, 0.86)} 64%, transparent 100%)`,
        content: '""',
        display: 'block',
        height: '3px',
        position: 'relative',
        width: '50%',
        zIndex: 1,
    },

    '&::after': {
        background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.accent.main, 0.34)}, transparent)`,
        content: '""',
        display: 'block',
        height: '1px',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
    },

    '&.active': {
        opacity: 1,
        transform: 'translateY(0)',
    },

    '@media (prefers-reduced-motion: reduce)': {
        '&::before': {
            animationDuration: '1400ms',
        },
    },

    [theme.breakpoints.up('md')]: {
        top: '64px',
    },
}))

const useDelayedActive = (active: boolean, delay: number) => {
    const [delayedActive, setDelayedActive] = useState(false)

    useEffect(() => {
        if (!active) {
            setDelayedActive(false)
            return
        }

        const timeout = window.setTimeout(() => {
            setDelayedActive(true)
        }, delay)

        return () => {
            window.clearTimeout(timeout)
        }
    }, [active, delay])

    return delayedActive
}

const isModifiedClick = (event: MouseEvent) => (
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey
)

const isNavigatingAnchorClick = (event: MouseEvent) => {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        isModifiedClick(event) ||
        !(event.target instanceof Element)
    ) {
        return false
    }

    const anchor = event.target.closest('a[href]')

    if (!(anchor instanceof HTMLAnchorElement)) {
        return false
    }

    const href = anchor.getAttribute('href')

    if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        anchor.hasAttribute('download') ||
        (anchor.target && anchor.target !== '_self')
    ) {
        return false
    }

    const targetUrl = new URL(anchor.href, window.location.href)

    if (targetUrl.origin !== window.location.origin) {
        return false
    }

    return (
        targetUrl.pathname !== window.location.pathname ||
        targetUrl.search !== window.location.search
    )
}

export const usePageNavigationFeedback = (enabled: boolean) => {
    const location = useLocation()
    const navigation = useNavigation()
    const [linkNavigationPending, setLinkNavigationPending] = useState(false)
    const navigationPending = enabled && (navigation.state !== 'idle' || linkNavigationPending)

    useEffect(() => {
        setLinkNavigationPending(false)
    }, [enabled, location.pathname, location.search, location.hash])

    useEffect(() => {
        if (!linkNavigationPending) {
            return
        }

        const timeout = window.setTimeout(() => {
            setLinkNavigationPending(false)
        }, LINK_NAVIGATION_PENDING_TIMEOUT)

        return () => {
            window.clearTimeout(timeout)
        }
    }, [linkNavigationPending])

    useEffect(() => {
        if (!enabled) {
            return
        }

        const onClick = (event: MouseEvent) => {
            if (isNavigatingAnchorClick(event)) {
                setLinkNavigationPending(true)
            }
        }

        document.addEventListener('click', onClick, { capture: true })

        return () => {
            document.removeEventListener('click', onClick, { capture: true })
        }
    }, [enabled])

    return useDelayedActive(navigationPending, NAVIGATION_FEEDBACK_DELAY)
}
